import React from 'react';
import { Eye, ChevronRight, Target, TrendingUp, Scale, Leaf, Zap, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const VisionSectionModern: React.FC = () => {
    const visionPoints = [
        {
            number: "01",
            title: "Électricité, Eau et Réformes",
            description: "Solutions immédiates pour l'accès universel à l'électricité et à l'eau",
            icon: Zap,
            color: "text-primary-blue",
            bgColor: "bg-primary-blue/10"
        },
        {
            number: "02",
            title: "Entrepreneuriat et Employabilité",
            description: "Soutien à l'entrepreneuriat et formation des jeunes",
            icon: TrendingUp,
            color: "text-primary-green",
            bgColor: "bg-primary-green/10"
        },
        {
            number: "03",
            title: "Logements et Infrastructures",
            description: "Modernisation des infrastructures de transport et numériques",
            icon: Target,
            color: "text-primary-yellow",
            bgColor: "bg-primary-yellow/10"
        },
        {
            number: "04",
            title: "Capital Humain et Justice Sociale",
            description: "Investissement dans la santé, l'éducation et l'inclusion",
            icon: Scale,
            color: "text-primary-blue",
            bgColor: "bg-primary-blue/10"
        },
        {
            number: "05",
            title: "Économie et Développement Durable",
            description: "Diversification économique et valorisation du capital naturel",
            icon: Leaf,
            color: "text-primary-green",
            bgColor: "bg-primary-green/10"
        },
        {
            number: "06",
            title: "Gouvernance et Institutions",
            description: "Restauration de l'État de droit et modernisation",
            icon: Shield,
            color: "text-primary-yellow",
            bgColor: "bg-primary-yellow/10"
        }
    ];

    return (
        <section id="vision" className="overflow-hidden">
            {/* Split layout */}
            <div className="flex flex-col lg:flex-row min-h-[600px]">

                {/* Partie gauche - Vision (fond vert) */}
                <div className="lg:w-1/2 bg-primary-green text-white p-12 lg:p-16 flex items-center">
                    <div className="w-full max-w-lg">
                        <div className="flex items-center space-x-2 mb-6">
                            <Eye className="w-6 h-6 text-primary-yellow" />
                            <span className="text-primary-yellow text-sm uppercase tracking-wider font-semibold">Notre Vision</span>
                        </div>

                        <h2 className="text-4xl lg:text-5xl font-light mb-8 leading-tight">
                            Un Gabon moderne et prospère
                        </h2>

                        <div className="w-16 h-1 bg-primary-yellow mb-8"></div>

                        <p className="text-xl font-light leading-relaxed mb-8 opacity-90">
                            "Ensemble, nous avons le pouvoir de bâtir le Gabon que nos enfants méritent"
                        </p>

                        <p className="text-base leading-relaxed opacity-80">
                            Une vision ambitieuse pour transformer notre nation à travers des réformes
                            profondes et une gouvernance exemplaire, plaçant le citoyen au cœur de
                            toutes nos actions.
                        </p>
                    </div>
                </div>

                {/* Partie droite - Liste avec icônes */}
                <div className="lg:w-1/2 bg-white p-12 lg:p-16 flex items-center">
                    <div className="w-full max-w-lg">
                        <h3 className="text-2xl font-light text-gray-900 mb-8">
                            Nos 6 piliers fondamentaux
                        </h3>

                        <div className="grid grid-cols-1 gap-4">
                            {visionPoints.map((point, index) => {
                                const Icon = point.icon;
                                return (
                                    <div
                                        key={index}
                                        className="flex items-start space-x-4 group cursor-pointer hover:transform hover:translate-x-2 transition-all duration-300"
                                    >
                                        <div className={`${point.bgColor} p-3 group-hover:scale-110 transition-transform flex-shrink-0`}>
                                            <Icon className={`w-6 h-6 ${point.color}`} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-2 mb-1">
                                                <span className="text-xs font-medium text-gray-400">{point.number}</span>
                                                <h4 className="text-base font-semibold text-gray-900 group-hover:text-primary-blue transition-colors">
                                                    {point.title}
                                                </h4>
                                            </div>
                                            <p className="text-xs text-gray-600 leading-relaxed">
                                                {point.description}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Section engagement - Pleine largeur en bas */}
            <div className="bg-gray-900 text-white py-12">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <h3 className="text-2xl font-light mb-4">Notre engagement</h3>
                        <div className="w-16 h-1 bg-primary-yellow mx-auto mb-6"></div>
                        <p className="text-lg font-light leading-relaxed mb-8 opacity-90 max-w-3xl mx-auto">
                            Transformer cette vision en réalité à travers des actions concrètes
                            et une gouvernance exemplaire pour tous les Gabonais.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <button
                                onClick={() => document.getElementById('adhesion')?.scrollIntoView({ behavior: 'smooth' })}
                                className="bg-primary-yellow text-gray-900 px-8 py-3 font-semibold hover:bg-white transition-colors inline-flex items-center space-x-2 group"
                            >
                                <span>REJOINDRE LE MOUVEMENT</span>
                                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>

                            <Link
                                to="/vision"
                                className="bg-primary-green text-white px-8 py-3 font-semibold hover:bg-green-600 transition-colors inline-flex items-center space-x-2 group"
                            >
                                <span>DÉCOUVRIR NOTRE PROGRAMME COMPLET</span>
                                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default VisionSectionModern;
